import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  Table as ReactTable,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { gql, useQuery, useMutation } from "@apollo/client";

import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { useUser } from "../../../user-context";

import { DataTableToolbar } from "./data-table-toolbar";
import { cn, isDefined } from "../../../utils";
import { HorizontalPadding } from "../../layout";
import { AppointmentsRow } from "./columns";
import { OvalSpinner } from "../../../components/loading";
import { getAppointmentStatus, GET_APPOINTMENT_VIEW_PREFERENCES } from "./list";
import { useFeatureFlags } from "../../../hooks";
import { ServiceTypeCode } from "../../patients/eligibilities/types";
import { UPDATE_APPOINTMENT_VIEW_PREFERENCES } from "../../../components/ui/table-helpers/data-table-view-options";
import { GetAppointmentViewPreferences } from "../../../generated/GetAppointmentViewPreferences";

interface DataTableProps<TValue> {
  columns: ColumnDef<AppointmentsRow, TValue>[];
  defaultColumnFilters?: ColumnFiltersState;
  data: AppointmentsRow[];
}

function useSkipper() {
  const shouldSkipRef = React.useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = React.useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  React.useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

export type DataTableMeta = {
  updateData: (rowIndex: number, columnId: string, value: any) => void;
  notesExpanded: boolean;
  setNotesExpanded: (expanded: boolean) => void;
  columnFilters: ColumnFiltersState;
};

export function DataTable<TValue>({
  columns,
  data,
  defaultColumnFilters,
  children,
  loading,
  loadingMore,
  date,
  setDate,
  onRowClick,
  refetch,
  nextPoll,
  appointmentViewPreferences,
}: DataTableProps<TValue> & {
  children?: (table: ReactTable<AppointmentsRow>) => React.ReactElement;
  loading?: boolean;
  loadingMore?: boolean;
  date: Date;
  setDate: (date: Date) => void;
  onRowClick: (row: AppointmentsRow) => void;
  refetch: () => void;
  nextPoll: Date;
  appointmentViewPreferences?: GetAppointmentViewPreferences;
}) {
  const flags = useFeatureFlags();
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [rowSelection, setRowSelection] = React.useState({});

  const appointmentViewPreferencesMap = Object.entries(
    appointmentViewPreferences?.user?.appointmentViewPreferences?.columns || {}
  ).reduce((acc: Record<string, boolean>, [key, value]: [string, any]) => {
    if (value.subLabels) {
      value.subLabels.forEach((subLabel: string) => {
        acc[subLabel] = value.visible;
      });
    }
    acc[key] = value.visible;
    return acc;
  }, {} as Record<string, boolean>);

  const [updateAppointmentViewPreferences] = useMutation(
    UPDATE_APPOINTMENT_VIEW_PREFERENCES
  );

  const user = useUser()!;

  const defaultColumnVisibility = {
    "Policy Confirmed": flags.defaultPolicyConfirmedColumnInAppointmentList,
    Benefits: false,
    accountType: false,
    appointmentLabels: false,
    "Next Appointment": false,
    "In Network": false,
    Location: false,
    ...Object.assign(
      {},
      ...user.organization.providerServiceConfiguration.map((config) => {
        if (config.serviceType === ServiceTypeCode.HealthBenefitPlanCoverage) {
          return {
            [config.name + " Ind Ded"]: false,
            [config.name + " Fam Ded"]: false,
            [config.name + " Ind OOP"]: false,
            [config.name + " Fam OOP"]: false,
          };
        } else {
          return {
            [config.name + " Copay"]: false,
            [config.name + " Coinsurance"]: false,
            [config.name + " Deductible"]: false,
            [config.name + " Out of Pocket"]: false,
            [config.name + " Visit Limit"]: false,
            [config.name + " Auth Required"]: false,
          };
        }
      })
    ),
  };

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      ...defaultColumnVisibility,
      ...appointmentViewPreferencesMap,
    });

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    (defaultColumnFilters ||
      appointmentViewPreferences?.user?.appointmentViewPreferences?.filters) ??
      []
  );

  React.useEffect(() => {
    setColumnVisibility({
      ...defaultColumnVisibility,
      ...appointmentViewPreferencesMap,
    });
    setColumnFilters(
      appointmentViewPreferences?.user?.appointmentViewPreferences?.filters ??
        []
    );
  }, [appointmentViewPreferences]);

  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: "Scheduled At",
      desc: false,
    },
  ]);

  const [notesExpanded, setNotesExpanded] = React.useState(false);

  const [tableData, setTableData] = React.useState(data);
  React.useEffect(() => {
    setTableData(data);
  }, [data]);
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    enableRowSelection: true,
    // Turn off pagination
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    autoResetPageIndex,
    meta: {
      updateData: (rowIndex: number, columnId: string, value: any) => {
        // Skip page index reset until after next rerender
        skipAutoResetPageIndex();
        setTableData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex]!,
                [columnId]: value,
              };
            }
            return row;
          })
        );
      },
      columnFilters,
      notesExpanded,
      setNotesExpanded,
    },
  });

  const actionColumnEnabled = table.getColumn("Actions")?.getIsVisible();

  return (
    <>
      {children && children(table)}
      <div className="space-y-4">
        <HorizontalPadding>
          <div className="w-full">
            <DataTableToolbar
              table={table}
              date={date}
              setDate={setDate}
              loading={loading || loadingMore}
              refetch={refetch}
              nextPoll={nextPoll}
              appointmentViewPreferences={appointmentViewPreferences}
            />
          </div>
        </HorizontalPadding>
        <div className="border bg-white max-h-[80vh] overflow-x-auto">
          <div className="relative w-full">
            <table className={cn("w-full caption-bottom text-sm")}>
              <TableHeader className="border-b">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          className="px-1.5 sticky h-20 top-0 z-[5] first:pl-2 bg-white"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="overflow-y-scroll">
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => {
                    const status = getAppointmentStatus(
                      row.original.appointment
                    );
                    return (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                        className={cn(
                          "hover:bg-slate-50 cursor-pointer border-l-[5px]",
                          status === "Past" && "border-l-sky-100",
                          status === "Current" && "border-l-sky-400",
                          status === "Upcoming" && "border-l-sky-600"
                        )}
                        onClick={() => {
                          onRowClick(row.original);
                        }}
                      >
                        {row.getVisibleCells().map((cell, i) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "whitespace-nowrap px-1 py-2 first:pl-2",
                              // CSS to make the last column sticky
                              actionColumnEnabled &&
                                "last:sticky last:right-0  last:bg-white last:drop-shadow"
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="w-full flex justify-center">
                        <OvalSpinner />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
